import { type ChangeEvent, type ReactElement, useCallback, useState } from 'react';

import { Button } from '@/ux-common/components/button/button';
import { Input, InputClear, InputField, InputLabel } from '@/ux-common/components/input/input';

import type { NumberRangeFilterProps } from '../types';

/**
 * Number range filter with equals, greaterThan, and lessThan inputs.
 * Matches the old data-table filter UI.
 */
export const NumberRangeFilter = ({
  value,
  onChange,
  onClear,
  isActive,
  onClose,
}: NumberRangeFilterProps & { isActive: boolean; onClose: () => void }): ReactElement => {
  // Local state for inputs
  const [equals, setEquals] = useState<string>(value.equals?.toString() ?? '');
  const [greaterThan, setGreaterThan] = useState<string>(value.greaterThan?.toString() ?? '');
  const [lessThan, setLessThan] = useState<string>(value.lessThan?.toString() ?? '');

  const handleNumberInput = (inputValue: string): number | undefined => {
    if (inputValue === '') return undefined;
    const num = parseFloat(inputValue);
    return isNaN(num) ? undefined : num;
  };

  const updateFilter = useCallback(
    (field: 'equals' | 'greaterThan' | 'lessThan', inputValue: string) => {
      const newValue = { ...value };
      const numValue = handleNumberInput(inputValue);

      if (numValue === undefined) {
        delete newValue[field];
      } else {
        newValue[field] = numValue;
      }

      // If all fields are empty, clear the filter completely
      if (Object.keys(newValue).length === 0) {
        onClear();
      } else {
        onChange(newValue);
      }
    },
    [value, onChange, onClear]
  );

  const handleEqualsChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setEquals(newValue);
      updateFilter('equals', newValue);
    },
    [updateFilter]
  );

  const handleGreaterThanChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setGreaterThan(newValue);
      updateFilter('greaterThan', newValue);
    },
    [updateFilter]
  );

  const handleLessThanChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLessThan(newValue);
      updateFilter('lessThan', newValue);
    },
    [updateFilter]
  );

  const clearField = useCallback(
    (field: 'equals' | 'greaterThan' | 'lessThan', e: React.MouseEvent) => {
      // The filter body lives inside a popover that closes on outside/ancestor
      // presses, so clearing must not bubble up to it.
      e.stopPropagation();
      if (field === 'equals') setEquals('');
      if (field === 'greaterThan') setGreaterThan('');
      if (field === 'lessThan') setLessThan('');
      updateFilter(field, '');
    },
    [updateFilter]
  );

  return (
    <>
      <div className="p-4 space-y-4">
        <div className="space-y-3">
          <Input className="input-reserve-clear">
            <InputLabel>Equals</InputLabel>
            <InputField
              type="number"
              placeholder="Enter value"
              value={equals}
              onChange={handleEqualsChange}
            >
              {equals && (
                <InputClear aria-label="Clear Equals" onClick={(e) => clearField('equals', e)} />
              )}
            </InputField>
          </Input>

          <Input className="input-reserve-clear">
            <InputLabel>Greater than</InputLabel>
            <InputField
              type="number"
              placeholder="Enter minimum value"
              value={greaterThan}
              onChange={handleGreaterThanChange}
            >
              {greaterThan && (
                <InputClear
                  aria-label="Clear Greater than"
                  onClick={(e) => clearField('greaterThan', e)}
                />
              )}
            </InputField>
          </Input>

          <Input className="input-reserve-clear">
            <InputLabel>Less than</InputLabel>
            <InputField
              type="number"
              placeholder="Enter maximum value"
              value={lessThan}
              onChange={handleLessThanChange}
            >
              {lessThan && (
                <InputClear
                  aria-label="Clear Less than"
                  onClick={(e) => clearField('lessThan', e)}
                />
              )}
            </InputField>
          </Input>
        </div>
      </div>

      {/* Footer with Clear/Close buttons */}
      <div className="flex justify-end p-3 border-t border-border">
        {isActive && (
          <Button
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setEquals('');
              setGreaterThan('');
              setLessThan('');
              onClear();
            }}
            className="btn btn-tier3 btn-sm mr-2 font-semibold"
          >
            Clear
          </Button>
        )}
        <Button onClick={onClose} className="btn btn-tier2 btn-sm font-semibold">
          Close
        </Button>
      </div>
    </>
  );
};

NumberRangeFilter.displayName = 'NumberRangeFilter';
